import React from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Stack,
  Paper,
  Divider,
} from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
  BugReport as BugIcon,
  Home as HomeIcon,
} from '@mui/icons-material';

class ErrorBoundaryWithRecovery extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Report to error tracking service if available
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: true,
        error_id: this.state.errorId,
      });
    }
  }

  handleRetry = () => {
    // Reset error state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });

    // Call retry callback if provided
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  handleReportError = () => {
    const { error, errorInfo, errorId } = this.state;

    // Create error report
    const errorReport = {
      id: errorId,
      message: error?.message || 'Unknown error',
      stack: error?.stack || 'No stack trace',
      componentStack: errorInfo?.componentStack || 'No component stack',
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    // Copy to clipboard for easy reporting
    navigator.clipboard.writeText(JSON.stringify(errorReport, null, 2))
      .then(() => {
        alert('Error report copied to clipboard. Please paste it when contacting support.');
      })
      .catch(() => {
        // Fallback: show error details in alert
        alert(`Error Report:\n\n${JSON.stringify(errorReport, null, 2)}`);
      });
  };

  handleGoHome = () => {
    if (this.props.onGoHome) {
      this.props.onGoHome();
    } else {
      window.location.href = '/';
    }
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo } = this.state;
      const {
        title, showDetails = false, showRetry = true, showGoHome = true,
      } = this.props;

      return (
        <Box sx={{ p: 3, maxWidth: '800px', mx: 'auto' }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Stack spacing={3} alignItems="center" textAlign="center">
              {/* Error Icon */}
              <ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />

              {/* Title */}
              <Typography variant="h4" component="h1" color="error.main">
                {title || 'Something went wrong'}
              </Typography>

              {/* Error Message */}
              <Alert severity="error" sx={{ width: '100%', textAlign: 'left' }}>
                <Typography variant="body1">
                  {error?.message || 'An unexpected error occurred. Please try again.'}
                </Typography>
              </Alert>

              {/* Error Details (if enabled) */}
              {showDetails && error && (
                <Box sx={{ width: '100%' }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Technical Details
                  </Typography>
                  <Alert severity="info" sx={{ textAlign: 'left' }}>
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.875rem',
                        fontFamily: 'monospace',
                      }}
                    >
                      {error.stack}
                    </Typography>
                  </Alert>

                  {errorInfo?.componentStack && (
                    <Alert severity="info" sx={{ mt: 1, textAlign: 'left' }}>
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          fontSize: '0.875rem',
                          fontFamily: 'monospace',
                        }}
                      >
                        Component Stack:
                        {errorInfo.componentStack}
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="center">
                {showRetry && (
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={this.handleRetry}
                    size="large"
                  >
                    Try Again
                  </Button>
                )}

                <Button
                  variant="outlined"
                  startIcon={<BugIcon />}
                  onClick={this.handleReportError}
                  size="large"
                >
                  Report Error
                </Button>

                {showGoHome && (
                  <Button
                    variant="text"
                    startIcon={<HomeIcon />}
                    onClick={this.handleGoHome}
                    size="large"
                  >
                    Go Home
                  </Button>
                )}
              </Stack>

              {/* Help Text */}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                If this problem persists, please contact support with the error report.
              </Typography>
            </Stack>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundaryWithRecovery;
